<template>
  <div v-if="tabs" :class="[activeTabName + '-tab', { 'tabs-content': !flat }]">
    <div>
      <slot name="card-header-top"></slot>
      <ul
        class="nav nav-tabs hidden md:flex"
        :class="{ 'pointer-events-none': !ready }"
        :aria-disabled="!ready"
      >
        <NavBarItem
          v-for="(tab, idx) in visibleTabs"
          :key="idx"
          :data-cy="`tab-${tab.name}`"
          :active="idx.toString() === activePos.toString()"
          :name="tab.name"
          :display-name="tab.displayName"
          :badge="tab.badge"
          @click="handleClick(tab.name)"
        />
      </ul>
      <div
        class="md:hidden -mx-8 p-8 border-t border-b border-gray-200 bg-gray-50"
        :class="{ 'pointer-events-none': !ready }"
        :aria-disabled="!ready"
      >
        <Select
          v-model="activeTabName"
          :options="tabOptions"
          :allow-empty="false"
        />
      </div>
    </div>
    <div class="tabs-body card-body px-0 pt-6 pb-0">
      <template v-if="renderAll">
        <component
          :is="tabs[idx].component"
          v-for="(tab, idx) in tabs"
          v-show="idx === activePos"
          :ref="tabs[idx].ref"
          :key="tabs[idx].name"
          v-bind="tabs[idx].props"
          v-on="tabs[idx].events"
        />
      </template>
      <keep-alive v-else-if="keepAlive">
        <component
          :is="currentTab.component"
          v-if="currentTab"
          :ref="currentTab.ref"
          :key="currentTab.name"
          v-bind="currentTab.props"
          v-on="currentTab.events"
        />
      </keep-alive>
      <component
        :is="currentTab.component"
        v-else-if="currentTab"
        :ref="currentTab.ref"
        :key="currentTab.name"
        v-bind="currentTab.props"
        v-on="currentTab.events"
      />
    </div>
  </div>
</template>

<script>
import { EventBus } from "@/lib/event_bus.js";
import { getQueryVariables, updateTabParam } from "@/lib/url.js";

import NavBarItem from "./NavBarItem.vue";

export default {
  components: {
    NavBarItem
  },
  emits: ["tab-change"],
  props: {
    tabs: { type: Array, default: () => [] },
    props: { type: Object, default: () => ({}) },
    // keepAlive is not required if renderAll is true
    keepAlive: { type: Boolean, default: false },
    // renderAll allows non-active tabs to render their bodies hidden
    // to allow immediately advancing their lifecycle to mounted
    renderAll: { type: Boolean, default: false },
    id: {
      type: String,
      default: Math.floor(Math.random() * 1000).toString()
    },
    flat: {
      type: Boolean,
      default: false
    },
    // When false, the tab strip renders but is non-interactive
    // (pointer-events-none) so a click cannot land while the tab set is still
    // settling and shifting positions. Consumers whose tabs are gated on async
    // permission data pass :ready once that data has resolved. Defaults true, so
    // consumers with a static tab set are unaffected.
    ready: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      activePos: 0,
      activeTabName: "",
      tabsId: null,
      // True once the user has actively chosen a tab (click / mobile select /
      // set-selected-tab). Distinguishes a real selection from setInitialTab's
      // auto-default, so an async tabs settle only preserves the former.
      userSelectedTab: false
    };
  },
  computed: {
    currentTab() {
      return this.visibleTabs[this.activePos];
    },
    tabOptions() {
      return this.visibleTabs.map((tab) => ({
        value: tab.name,
        label: tab.name
      }));
    },
    visibleTabs() {
      return this.tabs
        .filter((tab) => tab.visible !== false)
        .map((tab) => ({
          ...tab,
          events: tab.events || {}
        }));
    }
  },
  watch: {
    tabs(newValue, oldValue) {
      if (JSON.stringify(newValue) === JSON.stringify(oldValue)) return;

      // The tabs prop settles asynchronously (e.g. permission-gated tabs appear
      // once canAccess resolves). Preserve a selection the USER made: re-resolve
      // its position by name, since a tab appearing shifts indexes. Only guard a
      // genuine selection — an auto-default parked here by setInitialTab must
      // still yield to the real default/URL once late tabs arrive, so fall
      // through to setInitialTab when the user hasn't chosen or the tab is gone.
      const activeIdx = this.getIndexByName(this.activeTabName);
      if (this.userSelectedTab && this.activeTabName && activeIdx > -1) {
        this.activePos = activeIdx;
        return;
      }
      this.setInitialTab();
    },
    activeTabName(newValue, oldValue) {
      if (newValue !== oldValue && this.currentTab?.name !== newValue) {
        this.handleClick(newValue);
      }
    }
  },
  created() {
    this.setInitialTab();
  },
  mounted() {
    EventBus.$on("set-selected-tab", this.handleClick);
    window.addEventListener("popstate", this.setInitialTab);
  },
  beforeUnmount() {
    EventBus.$off("set-selected-tab", this.handleClick);
    window.removeEventListener("popstate", this.setInitialTab);
  },
  methods: {
    getCurrentTabRef() {
      return this.$refs[this.currentTab.ref];
    },
    getIndexByName(tabName, tabs = this.visibleTabs) {
      return tabs.findIndex(
        (el) => el?.name?.toLowerCase() === tabName?.toLowerCase()
      );
    },
    updateParams(tabName) {
      updateTabParam(this.id, tabName);
    },
    handleClick(tabName) {
      if (this.currentTab?.beforeTabChange) {
        if (!this.currentTab.beforeTabChange(this.getCurrentTabRef())) {
          return false;
        }
      }

      this.userSelectedTab = true;
      this.activePos = this.getIndexByName(tabName);
      this.activeTabName = tabName;
      this.updateParams(tabName);
      this.$emit("tab-change", tabName);
    },
    setInitialTab(e) {
      if (e?.state?.turboRefresh) return;

      const params = getQueryVariables();
      if (params.tab) {
        // Decode tab params and split
        const assignedTab = params.tab.split(",").some((tab) => {
          // Decode name of tab
          const sanitizedParam = window
            .decodeURIComponent(tab)
            .replace("+", " ");
          // Remove tab id to get tab name
          const details = sanitizedParam.split("_");
          const tabName = details[1];
          // Does tab exist?
          const index = this.getIndexByName(tabName, this.tabs);
          if (index > -1) {
            // Get tab index including hidden tabs
            this.activePos = this.getIndexByName(tabName);
            this.activeTabName = tabName;
            return true;
          }
        });
        if (!assignedTab) {
          const tab = window.encodeURIComponent(
            this.visibleTabs[this.activePos].name
          );
          this.activeTabName = tab;
        }
      } else {
        this.activePos = 0;
        this.activeTabName = (this.visibleTabs[0] || {}).name || "";
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.tabs-content {
  background-color: #fff;
  border-radius: 6px;
  border: 1px solid var(--sera-color-neutral-gray-med-gray-500, #c7cfd3);
  box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.15);
  padding: 24px;
}

div.card-header ul.card-header-tabs {
  margin-left: 0px !important;
}
</style>
